import { JSX } from 'preact';

import { EditorProps, ViewerProps, RichProp } from 'client/courses/components/page-builder';
import { ImgProp } from 'client/courses/components/page-builder/editable';
import { ContainerWrapper } from 'client/courses/components/page-builder/container-wrapper';
import { createGenerateState } from './common-patterns';
import { Color } from 'shared/colors';
import { AboutBlockState, PaddingSize } from 'server/types';

export const type = 'calendar-about';
export const name = 'About Me';

export const initialState: AboutBlockState = {
  photo:
    'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1061&q=80',
  nameRichText: '<h2>Amanda Chen</h2>',
  titleRichText: '<p>Leadership & Life Design Coach</p>',
  bioRichText:
    "<p>After 15 years in corporate leadership, I discovered my true calling: helping ambitious professionals design lives they genuinely love. I believe success without fulfillment is the ultimate failure.</p><p>My coaching philosophy centers on whole-person transformation. We don't just work on your career—we architect a life where your professional success amplifies your personal joy. Through our work together, you'll develop the clarity, courage, and systems to make bold moves that align with your deepest values.</p><p>I'm here to help you stop settling and start designing a life that makes you excited to wake up every morning.</p>",
  imageAspect: 'aspect-[4/3]',
};

export const generateState = createGenerateState(initialState);

export const initialBlockState = {
  type,
  bgcolor: Color.white,
  paddingt: 'md' as PaddingSize,
  paddingb: 'md' as PaddingSize,
};

export function MiniView(): JSX.Element {
  return (
    <span class="block py-4">
      <span class="w-4/6 mx-auto grid grid-cols-2 gap-3">
        <span class="flex flex-col">
          <span class="block bg-gray-500 opacity-75 rounded-sm h-1 mb-2 w-3/4"></span>
          <span class="block bg-gray-400 opacity-75 rounded-sm h-1 mb-2 w-1/2"></span>
          <span class="block bg-gray-400 opacity-50 rounded-sm h-1 mb-1 w-full"></span>
          <span class="block bg-gray-400 opacity-50 rounded-sm h-1 mb-1 w-5/6"></span>
          <span class="block bg-gray-400 opacity-50 rounded-sm h-1 w-4/5"></span>
        </span>
        <span class="bg-gray-600 opacity-50 rounded aspect-square flex items-center justify-center">
          <span class="bg-gray-400 rounded-full h-2 w-2"></span>
        </span>
      </span>
    </span>
  );
}

function View(props: EditorProps<AboutBlockState> | ViewerProps<AboutBlockState>): JSX.Element {
  const aspectClass = props.state.imageAspect || 'aspect-[4/3]';

  return (
    <ContainerWrapper>
      <div>
        <div class="grid @xl:grid-cols-2 gap-8 @xl:gap-16 items-start">
          {/* Left Column - Photo */}
          <div>
            <div class="max-w-md mx-auto @xl:max-w-none">
              <div class={`${aspectClass} rounded-2xl overflow-hidden shadow-xl`}>
                <ImgProp
                  {...props}
                  path="photo"
                  wrapperClass="w-full h-full"
                  class="w-full h-full object-cover"
                  overlayClass="rounded-2xl"
                  width="600"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Text Content */}
          <div>
            <RichProp
              {...props}
              path="nameRichText"
              class="font-bold @lg:text-5xl @md:text-4xl text-3xl text-gray-900"
            />
            <RichProp
              {...props}
              path="titleRichText"
              class="@md:text-xl text-lg text-purple-600 font-medium mb-4"
            />
            <RichProp
              {...props}
              path="bioRichText"
              class="@lg:text-lg text-base text-gray-700 leading-relaxed"
            />
          </div>
        </div>
      </div>
    </ContainerWrapper>
  );
}

export const Editor = View;
export const Viewer = View;
